// src/pages/LiveDutyView.tsx — the personal "Events Live" duty view (BUILD_PLAN
// PR I-4). Lives inside InfoHub but wears the Events dark "Live Day View" island
// palette (EL.dark*). Sign on/off (→ EventLiveShifts via the Events API), duties,
// schedule, briefings (→ BriefingAcks), who-to-call, lone-working safety check-in,
// map fragment, live feed. Ported from events-live/hub-live.jsx.
//
// Notes vs prototype (production data limits, documented):
//  - Sign-on location isn't captured in EventLiveShifts, so "Where" shows the
//    event line and the map is a generic NYMR schematic (no false "you are here").
//  - Lone Working is a client-side safety timer (matches the prototype); it isn't
//    yet wired to an outbound escalation text.

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { C, F, EL } from "../theme";
import { ElIcon } from "../eventslive/ElIcon";
import { ensureElKeyframes } from "../eventslive/elKeyframes";
import { fmtClock, fmtHM, fmtDuration, fmtDayChip, fmtDateRange } from "../eventslive/elFmt";
import {
  fetchOpenCall, fetchLive, signOn, signOff, ackBriefing, withdrawOffer,
  type LivePayload, type ELLiveAssignment, type ELBriefing, type ELScheduleItem, type ELContact, type ELEventEntry,
} from "../lib/events";

const dcard: React.CSSProperties = { background: EL.darkCard, border: `1px solid ${EL.darkLine}`, borderRadius: 14, padding: 16 };
const dhead: React.CSSProperties = { fontFamily: F.serif, fontSize: 15.5, fontWeight: 700, color: EL.darkInk, marginBottom: 12, paddingBottom: 9, borderBottom: `1px solid ${EL.darkLine}`, display: "flex", alignItems: "center", gap: 8 };

function useNow(intervalMs = 1000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const id = window.setInterval(() => setNow(new Date()), intervalMs); return () => window.clearInterval(id); }, [intervalMs]);
  return now;
}

export function LiveDutyView() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [eventId, setEventId] = useState<number | null>(null);
  const [myEvents, setMyEvents] = useState<ELEventEntry[] | null>(null);
  const [live, setLive] = useState<LivePayload | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [signOffOpen, setSignOffOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    ensureElKeyframes();
    let alive = true;
    fetchOpenCall()
      .then((d) => {
        if (!alive) return;
        // Confirmed (live-state) events are the ones with a duty view. A volunteer
        // can be on several at once; fall back to a single synth entry on old API.
        const entries: ELEventEntry[] = d.events ?? (d.event && (d.myConfirmed?.length ?? 0) > 0
          ? [{ event: d.event, call: d.call, roles: d.roles ?? [], myOffer: d.myOffer ?? null, myAssignments: d.myAssignments ?? [], myConfirmed: d.myConfirmed ?? [], signedOn: !!d.signedOn, signedOnAt: d.signedOnAt ?? null, state: "live" }]
          : []);
        const liveEvents = entries.filter((e) => (e.state ?? (e.myConfirmed?.length ? "live" : null)) === "live");
        setMyEvents(liveEvents);
        const paramId = Number(params.get("event")) || null;
        if (paramId && liveEvents.some((e) => e.event.id === paramId)) setEventId(paramId);
        else if (liveEvents.length === 1) setEventId(liveEvents[0]!.event.id);
        else if (liveEvents.length === 0) setEventId(0);
        // >1 with no valid ?event → leave eventId null → the picker renders
      })
      .catch(() => { if (alive) { setMyEvents([]); setEventId(0); } });
    return () => { alive = false; };
    // params (?event=) is read once on mount; re-running on change is unwanted.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const reload = useMemo(() => () => {
    if (!eventId) return Promise.resolve();
    return fetchLive(eventId).then((d) => setLive(d)).catch(() => setErr("Couldn't refresh your duty view."));
  }, [eventId]);

  useEffect(() => {
    if (eventId === null || eventId === 0) return;
    let alive = true;
    setLive(null);
    const load = () => fetchLive(eventId).then((d) => { if (alive) setLive(d); }).catch(() => { /* keep last view */ });
    load();
    const id = window.setInterval(load, 30_000);
    return () => { alive = false; window.clearInterval(id); };
  }, [eventId]);

  const doSignOn = () => {
    if (!eventId) return;
    setBusy(true); setErr(null);
    signOn(eventId).then(() => reload()).then(() => { setConfirmOpen(false); setBusy(false); })
      .catch(() => { setErr("Sign-on failed — you may be outside the event window."); setBusy(false); setConfirmOpen(false); });
  };
  const doSignOff = () => {
    if (!eventId) return;
    setBusy(true); setErr(null);
    signOff(eventId).then(() => reload()).then(() => { setSignOffOpen(false); setBusy(false); })
      .catch(() => { setErr("Sign-off failed — please try again."); setBusy(false); setSignOffOpen(false); });
  };
  const doAck = (topicId: number) => {
    if (!eventId) return;
    ackBriefing(eventId, topicId).then(() => reload()).catch(() => setErr("Couldn't record your acknowledgement."));
  };
  // Accept every outstanding briefing in one tap — volunteers found acking each
  // topic individually too fiddly. Acks are idempotent server-side, so this is
  // safe to fire across all un-acked (or stale) topics at once.
  const doAckAll = () => {
    if (!eventId || !live) return;
    const ids = live.briefings.filter((b) => b.ackState !== "acked").map((b) => b.topicId);
    if (ids.length === 0) return;
    setErr(null);
    Promise.all(ids.map((id) => ackBriefing(eventId, id)))
      .then(() => reload())
      .catch(() => setErr("Couldn't record your acknowledgements — please try again."));
  };
  // Withdraw entirely (confirmed volunteers who can no longer make it). Removes
  // their duties in Events + signs off any open shift, then Home reverts to the
  // call invitation. Backend is the same withdraw route the Crew Call page uses.
  const doWithdraw = () => {
    if (!eventId) return;
    setBusy(true); setErr(null);
    withdrawOffer(eventId).then(() => navigate("/"))
      .catch(() => { setErr("Couldn't withdraw — please try again."); setBusy(false); setWithdrawOpen(false); });
  };

  if (myEvents === null) return <FullDark title="Loading your duty view…" />;
  if (myEvents.length > 1 && (eventId === null || eventId === 0)) return <EventPicker events={myEvents} onPick={setEventId} onBack={() => navigate("/")} />;
  if (eventId === 0 || myEvents.length === 0) return <FullDark title="No live duty right now" sub="You're not confirmed on an upcoming event yet. When the Event Manager confirms your role, your duty view appears here." onBack={() => navigate("/")} />;
  if (!live) return <FullDark title="Loading your duty view…" />;

  const signedOn = live.signOn.isSignedOn;
  const hasRole = live.hasConfirmedRole;
  const roleLabel = [...new Set(live.myConfirmed.map((a) => a.roleName))].join(" + ");
  const whereLabel = "Your station"; // location isn't captured at sign-on (see header note)

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: EL.darkBg }}>
      {/* top bar */}
      <div style={{ flexShrink: 0, padding: "10px 16px 13px", background: EL.darkTop, borderBottom: `2px solid ${EL.brass}`, display: "flex", alignItems: "center", gap: 11 }}>
        <button onClick={() => navigate("/")} style={{ width: 36, height: 36, borderRadius: 10, border: "1px solid rgba(245,241,234,0.2)", background: "rgba(245,241,234,0.06)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}><ElIcon name="chevron" size={17} style={{ transform: "scaleX(-1)" }} color={EL.darkInk} /></button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <span style={{ fontFamily: F.body, fontSize: 9.5, fontWeight: 800, letterSpacing: 1.4, textTransform: "uppercase", color: EL.goldGlow }}>Events Live</span>
            {signedOn && <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontFamily: F.body, fontSize: 9.5, fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase", color: EL.connGreen }}><span style={{ width: 7, height: 7, borderRadius: "50%", background: EL.connGreen, animation: "el-conn 2s infinite" }} />On duty</span>}
          </div>
          <div style={{ fontFamily: F.serif, fontSize: 17, fontWeight: 700, color: EL.darkInk, lineHeight: 1.05, marginTop: 1 }}>{live.event.name}</div>
        </div>
        {(myEvents?.length ?? 0) > 1 && (
          <button onClick={() => { setLive(null); setEventId(null); }} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "7px 11px", borderRadius: 999, border: "1px solid rgba(240,217,138,0.34)", background: "rgba(240,217,138,0.12)", color: EL.goldGlow, fontFamily: F.body, fontSize: 11.5, fontWeight: 800, cursor: "pointer", flexShrink: 0 }}>
            <ElIcon name="layers" size={14} color={EL.goldGlow} /> Switch
          </button>
        )}
        <div style={{ textAlign: "right" }}>
          <div style={{ fontFamily: F.mono, fontSize: 11, fontWeight: 700, color: EL.goldGlow }}>{fmtDayChip(live.targetDate)}</div>
          {live.dayOfEvent != null && <div style={{ fontFamily: F.body, fontSize: 9.5, fontWeight: 700, color: "rgba(253,251,246,0.6)", letterSpacing: 0.4 }}>DAY {live.dayOfEvent} OF {live.totalDays}</div>}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "14px 15px 26px", display: "flex", flexDirection: "column", gap: 13 }}>
        {err && <div style={{ background: "rgba(239,83,80,0.16)", border: `1px solid ${EL.liveRed}`, color: "#ffd9d7", borderRadius: 12, padding: "10px 13px", fontFamily: F.body, fontSize: 12.5 }}>{err}</div>}

        {!hasRole
          ? <LiveNotReady />
          : !live.within
            ? <NotTodayCard />
            : (!signedOn
              ? <SignOnCard roleLabel={roleLabel} where={whereLabel} onSignOn={() => setConfirmOpen(true)} />
              : <OnDutyCard signedOnAt={live.signOn.signedOnAt} roleLabel={roleLabel} where={whereLabel} onSignOff={() => setSignOffOpen(true)} />)}

        {hasRole && live.within && signedOn && <LoneWorkingCard />}
        {hasRole && <DutiesChecklist assignments={live.myConfirmed} />}
        {live.contacts.length > 0 && <ContactsCard contacts={live.contacts} />}
        {live.schedule.length > 0 && <ScheduleCard schedule={live.schedule} />}
        {live.briefings.length > 0 && <BriefingsCard briefings={live.briefings} onAck={doAck} onAckAll={doAckAll} />}
        <MapFragment />
        {live.feed.length > 0 && <FeedCard feed={live.feed} />}

        {hasRole && (
          <button onClick={() => setWithdrawOpen(true)} style={{ marginTop: 4, width: "100%", background: "transparent", border: "1px solid rgba(239,83,80,0.45)", borderRadius: 11, padding: "11px", fontFamily: F.body, fontSize: 13, fontWeight: 700, color: "rgba(239,83,80,0.95)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 7 }}>
            <ElIcon name="x" size={15} color="rgba(239,83,80,0.95)" /> I can no longer make it
          </button>
        )}
      </div>

      {confirmOpen && <ConfirmFitnessSheet roleLabel={roleLabel} busy={busy} onCancel={() => setConfirmOpen(false)} onConfirm={doSignOn} />}
      {signOffOpen && <ConfirmSheet title="Sign off duty?" body="This ends your shift and clears any active Lone Working check-in." cta="Sign off" icon="signout" busy={busy} onCancel={() => setSignOffOpen(false)} onConfirm={doSignOff} />}
      {withdrawOpen && <ConfirmSheet danger title={`Withdraw from ${live.event.name}?`} body="This removes you from the event — including any duties you've been given — and lets the Event Manager know. You can offer again if a call is still open." cta={busy ? "Withdrawing…" : "Withdraw me"} icon="signout" busy={busy} onCancel={() => setWithdrawOpen(false)} onConfirm={doWithdraw} />}
    </div>
  );
}

function LiveNotReady() {
  return (
    <div style={{ background: "rgba(245,241,234,0.045)", border: "1px dashed rgba(240,217,138,0.4)", borderRadius: 16, padding: "22px 18px", textAlign: "center" }}>
      <div style={{ width: 52, height: 52, borderRadius: 14, background: "rgba(240,217,138,0.14)", border: "1px solid rgba(240,217,138,0.3)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}><ElIcon name="clock" size={26} color={EL.goldGlow} /></div>
      <div style={{ fontFamily: F.serif, fontSize: 18, fontWeight: 700, color: EL.darkInk }}>Not confirmed yet</div>
      <div style={{ fontFamily: F.body, fontSize: 13, color: "rgba(253,251,246,0.7)", marginTop: 6, lineHeight: 1.5, maxWidth: 280, margin: "6px auto 0" }}>Your duty view unlocks the moment the Event Manager confirms your role. You'll get a notification — then sign on right here.</div>
    </div>
  );
}

function NotTodayCard() {
  return (
    <div style={{ background: "rgba(245,241,234,0.045)", border: "1px dashed rgba(240,217,138,0.4)", borderRadius: 16, padding: "20px 18px", textAlign: "center" }}>
      <div style={{ width: 50, height: 50, borderRadius: 14, background: "rgba(240,217,138,0.14)", border: "1px solid rgba(240,217,138,0.3)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}><ElIcon name="calendar" size={24} color={EL.goldGlow} /></div>
      <div style={{ fontFamily: F.serif, fontSize: 18, fontWeight: 700, color: EL.darkInk }}>You're all set</div>
      <div style={{ fontFamily: F.body, fontSize: 13, color: "rgba(253,251,246,0.7)", marginTop: 6, lineHeight: 1.5, maxWidth: 300, margin: "6px auto 0" }}>Your duties are confirmed. Sign-on opens on the day — check the schedule and briefings below to get ready.</div>
    </div>
  );
}

function SignOnCard({ roleLabel, where, onSignOn }: { roleLabel: string; where: string; onSignOn: () => void }) {
  return (
    <div style={{ background: "linear-gradient(180deg,#8C6210 0%,#6F4A0A 100%)", borderRadius: 16, border: "1.5px solid #5C3D08", padding: "18px 18px 16px", boxShadow: "0 4px 14px rgba(122,84,11,0.32)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <span style={{ width: 50, height: 50, borderRadius: 14, background: "rgba(244,226,168,0.16)", border: "1px solid rgba(244,226,168,0.32)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><ElIcon name="clock" size={25} color={C.brassGlow} /></span>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: F.serif, fontSize: 21, fontWeight: 700, color: C.brassGlow, letterSpacing: -0.4, lineHeight: 1.05 }}>Sign on for duty</div>
          <div style={{ fontFamily: F.body, fontSize: 11, fontWeight: 800, letterSpacing: 1.2, textTransform: "uppercase", color: "rgba(244,226,168,0.72)", marginTop: 5 }}>Pre-filled from your roster</div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginTop: 13, paddingTop: 12, borderTop: "1px dashed rgba(244,226,168,0.32)" }}>
        <PreFillPill label="Role" value={roleLabel} />
        <PreFillPill label="Where" value={where} />
      </div>
      <button onClick={onSignOn} style={{ width: "100%", marginTop: 14, minHeight: 48, borderRadius: 12, border: "none", background: C.brassGlow, color: "#5C3D08", fontFamily: F.body, fontSize: 15, fontWeight: 800, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.25)" }}>
        Confirm fitness &amp; sign on <ElIcon name="arrowRight" size={18} color="#5C3D08" />
      </button>
    </div>
  );
}
function PreFillPill({ label, value }: { label: string; value: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "7px 11px", borderRadius: 999, background: "rgba(255,255,255,0.1)", border: "1px solid rgba(244,226,168,0.28)" }}>
      <span style={{ fontFamily: F.mono, fontSize: 8.5, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", color: "rgba(244,226,168,0.7)" }}>{label}</span>
      <span style={{ fontFamily: F.body, fontSize: 12.5, fontWeight: 700, color: C.brassGlow }}>{value}</span>
    </span>
  );
}

function OnDutyCard({ signedOnAt, roleLabel, where, onSignOff }: { signedOnAt: string | null; roleLabel: string; where: string; onSignOff: () => void }) {
  const now = useNow(1000);
  const startMs = useMemo(() => (signedOnAt ? new Date(signedOnAt).getTime() : now.getTime()), [signedOnAt]); // eslint-disable-line react-hooks/exhaustive-deps
  const durMin = Math.max(0, Math.floor((now.getTime() - startMs) / 60000));
  return (
    <div style={{ background: "linear-gradient(180deg, rgba(110,140,114,0.18) 0%, rgba(79,104,82,0.1) 100%)", border: `1.5px solid ${C.sage}`, borderRadius: 16, padding: "15px 16px 14px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 10 }}>
        <span style={{ width: 10, height: 10, borderRadius: "50%", background: EL.connGreen, animation: "el-pulse-sage 2.4s infinite" }} />
        <span style={{ flex: 1, fontFamily: F.body, fontSize: 10.5, fontWeight: 800, letterSpacing: 1.5, textTransform: "uppercase", color: C.sageLt }}>On duty</span>
        <span style={{ fontFamily: F.mono, fontSize: 14, fontWeight: 700, color: EL.darkInk }}>{fmtDuration(durMin)}</span>
      </div>
      <div style={{ fontFamily: F.mono, fontSize: 38, fontWeight: 700, color: EL.darkInk, letterSpacing: -1, lineHeight: 1 }}>{fmtClock(now)}</div>
      {signedOnAt && <div style={{ fontFamily: F.serif, fontStyle: "italic", fontSize: 12.5, fontWeight: 600, color: C.sageLt, marginTop: 3 }}>signed on at {fmtHM(new Date(signedOnAt))}</div>}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", padding: "11px 0", borderTop: "1px dashed rgba(188,208,189,0.3)", borderBottom: "1px dashed rgba(188,208,189,0.3)", margin: "11px 0" }}>
        <DutyPill label="Role" value={roleLabel} /><DutyPill label="Where" value={where} />
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 10px", background: C.sage, color: "#fff", borderRadius: 999, fontFamily: F.body, fontSize: 11.5, fontWeight: 800 }}><ElIcon name="check" size={12} color="#fff" strokeWidth={2.6} /> Confirmed fit</span>
      </div>
      <button onClick={onSignOff} style={{ width: "100%", background: "rgba(245,241,234,0.06)", border: `1.5px solid ${C.sage}`, borderRadius: 11, padding: "11px", fontFamily: F.body, fontSize: 14, fontWeight: 800, color: EL.darkInk, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}><ElIcon name="signout" size={16} color={EL.darkInk} /> Sign off duty</button>
    </div>
  );
}
function DutyPill({ label, value }: { label: string; value: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 10px", background: "rgba(245,241,234,0.08)", border: "1px solid rgba(188,208,189,0.28)", borderRadius: 999 }}>
      <span style={{ fontFamily: F.mono, fontSize: 8.5, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", color: C.sageLt }}>{label}</span>
      <span style={{ fontFamily: F.body, fontSize: 12, fontWeight: 700, color: EL.darkInk }}>{value}</span>
    </span>
  );
}

function LoneWorkingCard() {
  const now = useNow(1000);
  const [lw, setLw] = useState<{ windowMin: number; dueAt: number } | null>(null);
  const windows = [120, 240, 360];
  if (!lw) {
    return (
      <button onClick={() => setLw({ windowMin: 240, dueAt: Date.now() + 240 * 60000 })}
        style={{ display: "flex", alignItems: "center", gap: 11, width: "100%", padding: "12px 14px", borderRadius: 13, cursor: "pointer", border: "1.5px dashed rgba(217,180,102,0.5)", background: "rgba(122,84,11,0.14)", textAlign: "left" }}>
        <span style={{ width: 12, height: 12, borderRadius: "50%", background: "rgba(245,241,234,0.25)", flexShrink: 0 }} />
        <span style={{ flex: 1 }}>
          <span style={{ display: "block", fontFamily: F.serif, fontSize: 14, fontWeight: 700, color: EL.darkInk }}>Lone Working</span>
          <span style={{ display: "block", fontFamily: F.body, fontSize: 11, fontWeight: 600, color: "rgba(217,180,102,0.9)", marginTop: 1 }}>Off · tap to enable a safety check-in</span>
        </span>
        <ElIcon name="shield" size={18} color={C.brassLt} />
      </button>
    );
  }
  const remaining = Math.max(0, lw.dueAt - now.getTime());
  const hh = String(Math.floor(remaining / 3600000)).padStart(2, "0");
  const mm = String(Math.floor((remaining % 3600000) / 60000)).padStart(2, "0");
  const ss = String(Math.floor((remaining % 60000) / 1000)).padStart(2, "0");
  return (
    <div style={{ background: "rgba(122,84,11,0.2)", border: `1.5px solid ${C.brass}`, borderRadius: 14, padding: "13px 15px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 9 }}>
        <span style={{ width: 11, height: 11, borderRadius: "50%", background: C.brassLt, animation: "el-pulse 2s infinite" }} />
        <span style={{ flex: 1, fontFamily: F.serif, fontSize: 14, fontWeight: 700, color: EL.darkInk }}>Lone Working — ON</span>
        <button onClick={() => setLw(null)} style={{ fontFamily: F.body, fontSize: 11, fontWeight: 800, color: C.brassLt, background: "none", border: "none", cursor: "pointer" }}>Switch off</button>
      </div>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontFamily: F.body, fontSize: 9.5, fontWeight: 800, letterSpacing: 1.3, textTransform: "uppercase", color: C.brassLt }}>Check-in due in</div>
        <div style={{ fontFamily: F.mono, fontSize: 30, fontWeight: 700, color: EL.darkInk, letterSpacing: -1, margin: "3px 0" }}>{hh}:{mm}:{ss}</div>
        <div style={{ fontFamily: F.serif, fontStyle: "italic", fontSize: 11.5, color: "rgba(253,251,246,0.7)", lineHeight: 1.4 }}>If not switched off, your duty manager will be alerted.</div>
      </div>
      <div style={{ display: "flex", gap: 6, marginTop: 11 }}>
        {windows.map((w) => { const sel = w === lw.windowMin; return (
          <button key={w} onClick={() => !sel && setLw({ windowMin: w, dueAt: Date.now() + w * 60000 })} style={{ flex: 1, padding: "7px 4px", borderRadius: 10, border: `1.5px solid ${sel ? C.brass : "rgba(245,241,234,0.18)"}`, background: sel ? C.brass : "rgba(245,241,234,0.05)", cursor: sel ? "default" : "pointer" }}>
            <div style={{ fontFamily: F.mono, fontSize: 13.5, fontWeight: 700, color: sel ? "#3a2806" : EL.darkInk }}>{w / 60} h</div>
          </button>
        ); })}
      </div>
    </div>
  );
}

function DutiesChecklist({ assignments }: { assignments: ELLiveAssignment[] }) {
  const tasks = useMemo(() => {
    const out: { key: string; role: string; text: string }[] = [];
    const seen = new Set<string>();
    for (const a of assignments) {
      a.duties.forEach((d, i) => {
        const key = `${a.roleId}-${i}-${d.slice(0, 12)}`;
        if (seen.has(key)) return; seen.add(key);
        out.push({ key, role: a.roleName, text: d });
      });
    }
    return out;
  }, [assignments]);
  const [done, setDone] = useState<Set<string>>(new Set());
  const toggle = (k: string) => setDone((p) => { const n = new Set(p); n.has(k) ? n.delete(k) : n.add(k); return n; });
  if (tasks.length === 0) return null;
  return (
    <div style={dcard}>
      <div style={dhead}><ElIcon name="clipboard" size={16} color={EL.goldGlow} /> My duties <span style={{ marginLeft: "auto", fontFamily: F.mono, fontSize: 12, fontWeight: 700, color: done.size === tasks.length ? EL.connGreen : "rgba(253,251,246,0.6)" }}>{done.size}/{tasks.length}</span></div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {tasks.map((t) => { const on = done.has(t.key); return (
          <button key={t.key} onClick={() => toggle(t.key)} style={{ display: "flex", gap: 11, alignItems: "flex-start", textAlign: "left", border: "none", background: "none", cursor: "pointer", padding: 0 }}>
            <span style={{ width: 22, height: 22, borderRadius: 7, flexShrink: 0, border: `1.5px solid ${on ? EL.connGreen : "rgba(245,241,234,0.25)"}`, background: on ? "rgba(128,232,168,0.18)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1 }}>{on && <ElIcon name="check" size={14} color={EL.connGreen} strokeWidth={2.6} />}</span>
            <span style={{ flex: 1 }}>
              <span style={{ display: "block", fontFamily: F.body, fontSize: 13.5, fontWeight: 600, color: on ? "rgba(253,251,246,0.5)" : EL.darkInk, textDecoration: on ? "line-through" : "none", lineHeight: 1.35 }}>{t.text}</span>
              <span style={{ fontFamily: F.mono, fontSize: 9.5, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: "rgba(217,180,102,0.8)" }}>{t.role}</span>
            </span>
          </button>
        ); })}
      </div>
    </div>
  );
}

function ContactsCard({ contacts }: { contacts: ELContact[] }) {
  const short = (role: string) => role.split(/\s+/).map((w) => w[0]).join("").slice(0, 3).toUpperCase();
  return (
    <div style={dcard}>
      <div style={dhead}><ElIcon name="phone" size={15} color={EL.goldGlow} /> Who to call</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {contacts.map((p, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 11 }}>
            <span style={{ width: 38, height: 38, borderRadius: 11, background: "rgba(217,180,102,0.16)", border: "1px solid rgba(217,180,102,0.3)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: F.mono, fontSize: 11, fontWeight: 700, color: EL.goldGlow }}>{short(p.role)}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: F.body, fontSize: 14, fontWeight: 700, color: EL.darkInk }}>{p.name ?? "—"}</div>
              <div style={{ fontFamily: F.body, fontSize: 11, color: "rgba(253,251,246,0.55)" }}>{p.role}{p.phone ? ` · ${p.phone}` : ""}</div>
            </div>
            {p.phone && <a href={`tel:${p.phone}`} style={{ width: 38, height: 38, borderRadius: 11, background: EL.brass, display: "flex", alignItems: "center", justifyContent: "center", textDecoration: "none" }}><ElIcon name="phone" size={16} color="#fff" /></a>}
          </div>
        ))}
      </div>
    </div>
  );
}

function ScheduleCard({ schedule }: { schedule: ELScheduleItem[] }) {
  const now = useNow(30_000);
  const status = (time: string): "done" | "now" | "next" | "" => {
    const parts = time.split(":").map(Number); const rowMin = (parts[0] || 0) * 60 + (parts[1] || 0);
    const nowMin = now.getHours() * 60 + now.getMinutes(); const d = rowMin - nowMin;
    if (d < -10) return "done"; if (d >= -10 && d <= 5) return "now"; if (d > 5 && d <= 45) return "next"; return "";
  };
  return (
    <div style={dcard}>
      <div style={dhead}><ElIcon name="train" size={16} color={EL.goldGlow} /> Today's schedule</div>
      <div style={{ display: "flex", flexDirection: "column" }}>
        {schedule.map((it, i) => {
          const st = status(it.time);
          const c = st === "done" ? "rgba(253,251,246,0.4)" : st === "now" ? EL.liveRed : EL.darkInk;
          return (
            <div key={it.id} style={{ display: "grid", gridTemplateColumns: "52px 1fr auto", gap: 10, padding: "9px 0", borderBottom: i === schedule.length - 1 ? "none" : "1px solid rgba(245,241,234,0.08)", alignItems: "center" }}>
              <div style={{ fontFamily: F.mono, fontSize: 13, fontWeight: 700, color: c }}>{it.time}</div>
              <div>
                <div style={{ fontFamily: F.body, fontSize: 13, fontWeight: 700, color: c, textDecoration: st === "done" ? "line-through" : "none" }}>{it.label}</div>
                <div style={{ fontFamily: F.body, fontSize: 11, color: "rgba(253,251,246,0.5)" }}>{[it.origin, it.destination].filter(Boolean).join(" → ")}{it.loco ? ` · ${it.loco}` : ""}</div>
              </div>
              {st === "now" && <span style={{ fontFamily: F.body, fontSize: 9.5, fontWeight: 800, letterSpacing: 0.6, color: "#fff", background: EL.liveRed, borderRadius: 6, padding: "3px 7px" }}>NOW</span>}
              {st === "next" && <span style={{ fontFamily: F.body, fontSize: 9.5, fontWeight: 800, letterSpacing: 0.6, color: EL.darkInk, border: "1px solid rgba(245,241,234,0.3)", borderRadius: 6, padding: "3px 7px" }}>NEXT</span>}
              {st === "done" && <ElIcon name="check" size={14} color="rgba(253,251,246,0.4)" />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BriefingsCard({ briefings, onAck, onAckAll }: { briefings: ELBriefing[]; onAck: (topicId: number) => void; onAckAll: () => void }) {
  const summarise = (md: string) => { const t = md.replace(/[#*_>`-]/g, "").replace(/\s+/g, " ").trim(); return t.length > 130 ? t.slice(0, 130) + "…" : t; };
  const outstanding = briefings.filter((b) => b.ackState !== "acked").length;
  return (
    <div style={dcard}>
      <div style={dhead}><ElIcon name="shield" size={16} color={EL.goldGlow} /> Briefings to acknowledge</div>
      {outstanding >= 2 && (
        <button onClick={onAckAll} style={{ width: "100%", marginBottom: 10, border: `1px solid ${EL.goldGlow}`, background: "rgba(217,180,102,0.18)", color: EL.goldGlow, borderRadius: 10, padding: "10px", fontFamily: F.body, fontSize: 13, fontWeight: 800, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7 }}>
          <ElIcon name="check" size={15} color={EL.goldGlow} strokeWidth={2.6} /> Accept all {outstanding} briefings
        </button>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {briefings.map((b) => (
          <div key={b.topicId} style={{ border: `1px solid ${b.ackState === "acked" ? "rgba(128,232,168,0.3)" : "rgba(245,241,234,0.13)"}`, borderRadius: 12, padding: "11px 12px", background: b.ackState === "acked" ? "rgba(128,232,168,0.06)" : "rgba(245,241,234,0.02)" }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 9 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: F.body, fontSize: 13.5, fontWeight: 800, color: EL.darkInk }}>{b.title}</div>
                <div style={{ fontFamily: F.body, fontSize: 11.5, color: "rgba(253,251,246,0.6)", marginTop: 2, lineHeight: 1.4 }}>{summarise(b.bodyMarkdown)}</div>
              </div>
              {b.ackState === "acked" && <span style={{ fontFamily: F.body, fontSize: 9.5, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase", color: EL.connGreen, display: "inline-flex", alignItems: "center", gap: 4, flexShrink: 0 }}><ElIcon name="check" size={12} color={EL.connGreen} strokeWidth={2.6} /> Read</span>}
            </div>
            {b.ackState !== "acked" && (
              <button onClick={() => onAck(b.topicId)} style={{ marginTop: 9, width: "100%", border: `1px solid ${EL.goldGlow}`, background: "rgba(217,180,102,0.12)", color: EL.goldGlow, borderRadius: 9, padding: "8px", fontFamily: F.body, fontSize: 12.5, fontWeight: 800, cursor: "pointer" }}>{b.ackState === "stale" ? "Updated — re-read & acknowledge" : "I've read & understood"}</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function MapFragment() {
  const stations = ["Pickering", "Levisham", "Goathland", "Grosmont", "Whitby"];
  return (
    <div style={dcard}>
      <div style={dhead}><ElIcon name="mapPin" size={15} color={EL.goldGlow} /> The line</div>
      <svg viewBox="0 0 340 70" style={{ width: "100%", height: "auto" }}>
        <line x1="20" y1="38" x2="320" y2="38" stroke="rgba(245,241,234,0.25)" strokeWidth="3" strokeLinecap="round" />
        {stations.map((st, i) => {
          const x = 20 + (i * 300) / (stations.length - 1);
          return (
            <g key={st}>
              <circle cx={x} cy="38" r="5" fill="rgba(245,241,234,0.5)" />
              <text x={x} y="60" textAnchor="middle" fontFamily="'IBM Plex Mono', monospace" fontSize="9" fill="rgba(253,251,246,0.55)">{st}</text>
            </g>
          );
        })}
      </svg>
      <div style={{ fontFamily: F.serif, fontStyle: "italic", fontSize: 12, color: "rgba(253,251,246,0.6)", marginTop: 4 }}>NYMR runs Pickering ↔ Whitby. Your station and muster point are confirmed with your duty.</div>
    </div>
  );
}

function FeedCard({ feed }: { feed: LivePayload["feed"] }) {
  return (
    <div style={dcard}>
      <div style={dhead}><span style={{ width: 8, height: 8, borderRadius: "50%", background: EL.liveRed, animation: "el-pulse-red 1.5s infinite" }} /> Live activity</div>
      <div style={{ display: "flex", flexDirection: "column", maxHeight: 200, overflowY: "auto" }}>
        {feed.slice(0, 8).map((e, i) => (
          <div key={e.id} style={{ display: "grid", gridTemplateColumns: "46px 1fr", gap: 9, padding: "8px 0", borderBottom: i === Math.min(7, feed.length - 1) ? "none" : "1px solid rgba(245,241,234,0.08)" }}>
            <div style={{ fontFamily: F.mono, fontSize: 11, color: "rgba(253,251,246,0.5)", paddingTop: 1 }}>{fmtHM(new Date(e.occurredAt))}</div>
            <div><div style={{ fontFamily: F.body, fontSize: 12.5, color: EL.darkInk, lineHeight: 1.35 }}>{e.summary}</div>{e.actorName && <div style={{ fontFamily: F.body, fontSize: 10.5, color: "rgba(253,251,246,0.45)", marginTop: 1 }}>{e.actorName}</div>}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- sheets ---------------------------------------------------------------
function ConfirmFitnessSheet({ roleLabel, busy, onCancel, onConfirm }: { roleLabel: string; busy: boolean; onCancel: () => void; onConfirm: () => void }) {
  return (
    <div onClick={onCancel} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "flex-end", zIndex: 60, animation: "el-fade .2s ease" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", background: "#22251f", borderRadius: "22px 22px 0 0", padding: "18px 20px 26px", borderTop: `2px solid ${C.sage}`, animation: "el-sheet-up .3s cubic-bezier(.2,.8,.2,1)" }}>
        <div style={{ width: 38, height: 4, borderRadius: 2, background: "rgba(245,241,234,0.25)", margin: "-6px auto 16px" }} />
        <div style={{ width: 54, height: 54, borderRadius: 15, background: "rgba(110,140,114,0.2)", border: `1.5px solid ${C.sage}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}><ElIcon name="heart" size={26} color={C.sageLt} /></div>
        <div style={{ textAlign: "center", fontFamily: F.body, fontSize: 10, fontWeight: 800, letterSpacing: 1.5, textTransform: "uppercase", color: C.sageLt }}>Declaration of fitness for duty</div>
        <div style={{ textAlign: "center", fontFamily: F.serif, fontSize: 19, fontStyle: "italic", fontWeight: 700, color: EL.darkInk, margin: "4px 0 12px" }}>Please read carefully</div>
        <div style={{ background: "rgba(245,241,234,0.05)", border: "1px dashed rgba(188,208,189,0.4)", borderRadius: 12, padding: 14, fontFamily: F.serif, fontSize: 13.5, color: EL.darkInk, lineHeight: 1.5 }}>
          I confirm I am fit for duty as <strong>{roleLabel}</strong>, free from the effects of alcohol or drugs, and have read the safety information for this event. I will report any concern to the Manager on Duty.
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
          <button onClick={onCancel} disabled={busy} style={{ flex: 1, minHeight: 48, borderRadius: 12, border: "1px solid rgba(245,241,234,0.25)", background: "transparent", color: "rgba(253,251,246,0.8)", fontFamily: F.body, fontSize: 14.5, fontWeight: 800, cursor: "pointer" }}>Not now</button>
          <button onClick={onConfirm} disabled={busy} style={{ flex: 1.4, minHeight: 48, borderRadius: 12, border: "none", background: C.sage, color: "#fff", fontFamily: F.body, fontSize: 14.5, fontWeight: 800, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 7, opacity: busy ? 0.6 : 1 }}><ElIcon name="check" size={16} color="#fff" strokeWidth={2.6} /> {busy ? "Signing on…" : "Confirm & sign on"}</button>
        </div>
      </div>
    </div>
  );
}

function ConfirmSheet({ title, body, cta, icon, busy, danger, onCancel, onConfirm }: { title: string; body: string; cta: string; icon?: string; busy: boolean; danger?: boolean; onCancel: () => void; onConfirm: () => void }) {
  const accent = danger ? EL.liveRed : C.sage;
  return (
    <div onClick={onCancel} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 22, zIndex: 60, animation: "el-fade .2s ease" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 320, background: "#22251f", borderRadius: 20, padding: "20px 18px 16px", border: `1.5px solid ${accent}` }}>
        <div style={{ textAlign: "center", fontFamily: F.serif, fontSize: 19, fontStyle: "italic", fontWeight: 700, color: EL.darkInk, marginBottom: 6 }}>{title}</div>
        <div style={{ textAlign: "center", fontFamily: F.serif, fontStyle: "italic", fontSize: 13, color: "rgba(253,251,246,0.7)", lineHeight: 1.5, marginBottom: 16 }}>{body}</div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onCancel} disabled={busy} style={{ flex: 1, minHeight: 46, borderRadius: 12, border: "1px solid rgba(245,241,234,0.25)", background: "transparent", color: "rgba(253,251,246,0.8)", fontFamily: F.body, fontSize: 14, fontWeight: 800, cursor: "pointer" }}>Cancel</button>
          <button onClick={onConfirm} disabled={busy} style={{ flex: 1, minHeight: 46, borderRadius: 12, border: "none", background: accent, color: "#fff", fontFamily: F.body, fontSize: 14, fontWeight: 800, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 7, opacity: busy ? 0.6 : 1 }}>{icon && <ElIcon name={icon} size={15} color="#fff" />} {cta}</button>
        </div>
      </div>
    </div>
  );
}

// Multi-event picker — shown when the caller is confirmed on more than one event.
// Each card opens that event's duty view (works pre-event: it shows the plan).
function EventPicker({ events, onPick, onBack }: { events: ELEventEntry[]; onPick: (id: number) => void; onBack: () => void }) {
  const todayIso = new Date().toISOString().slice(0, 10);
  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: EL.darkBg }}>
      <div style={{ flexShrink: 0, padding: "10px 16px 13px", background: EL.darkTop, borderBottom: `2px solid ${EL.brass}`, display: "flex", alignItems: "center", gap: 11 }}>
        <button onClick={onBack} style={{ width: 36, height: 36, borderRadius: 10, border: "1px solid rgba(245,241,234,0.2)", background: "rgba(245,241,234,0.06)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}><ElIcon name="chevron" size={17} style={{ transform: "scaleX(-1)" }} color={EL.darkInk} /></button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: F.body, fontSize: 9.5, fontWeight: 800, letterSpacing: 1.4, textTransform: "uppercase", color: EL.goldGlow }}>Events Live</div>
          <div style={{ fontFamily: F.serif, fontSize: 17, fontWeight: 700, color: EL.darkInk, lineHeight: 1.05, marginTop: 1 }}>Choose your event</div>
        </div>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "14px 15px 26px", display: "flex", flexDirection: "column", gap: 11 }}>
        <div style={{ fontFamily: F.body, fontSize: 13, color: "rgba(253,251,246,0.7)", lineHeight: 1.5 }}>You're confirmed on {events.length} events. Pick one to see its duty view, schedule and briefings.</div>
        {events.map((en) => {
          const roleLabel = [...new Set(en.myConfirmed.map((a) => a.roleName))].join(" + ");
          const isToday = en.event.days.includes(todayIso);
          const upcoming = en.event.days.length > 0 && en.event.days[0]! > todayIso;
          const tag = en.signedOn ? "On duty now" : isToday ? "Today" : upcoming ? "Upcoming" : "Today";
          const tagColor = en.signedOn ? EL.connGreen : isToday ? EL.liveRed : EL.goldGlow;
          return (
            <button key={en.event.id} onClick={() => onPick(en.event.id)} style={{ textAlign: "left", display: "flex", alignItems: "center", gap: 13, background: "rgba(245,241,234,0.045)", border: `1px solid ${EL.darkLine}`, borderRadius: 14, padding: "14px 15px", cursor: "pointer" }}>
              <span style={{ width: 44, height: 44, borderRadius: 12, background: "rgba(240,217,138,0.14)", border: "1px solid rgba(240,217,138,0.3)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, position: "relative" }}>
                <ElIcon name="shieldCheck" size={22} color={EL.goldGlow} />
                {en.signedOn && <span style={{ position: "absolute", top: -3, right: -3, width: 11, height: 11, borderRadius: "50%", background: EL.connGreen, border: "2px solid #1a1a1a" }} />}
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "block", fontFamily: F.serif, fontSize: 17, fontWeight: 700, color: EL.darkInk, lineHeight: 1.1 }}>{en.event.name}</span>
                <span style={{ display: "block", fontFamily: F.body, fontSize: 12, color: "rgba(253,251,246,0.7)", marginTop: 2 }}>{fmtDateRange(en.event.days)}{roleLabel ? ` · ${roleLabel}` : ""}</span>
                <span style={{ display: "inline-block", marginTop: 6, fontFamily: F.body, fontSize: 9.5, fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase", color: tagColor }}>{tag}</span>
              </span>
              <ElIcon name="chevron" size={18} color="rgba(253,251,246,0.5)" />
            </button>
          );
        })}
      </div>
    </div>
  );
}

function FullDark({ title, sub, onBack }: { title: string; sub?: string; onBack?: () => void }) {
  return (
    <div style={{ minHeight: "100vh", background: EL.darkBg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, padding: 24, textAlign: "center" }}>
      <div style={{ fontFamily: F.serif, fontSize: 20, fontWeight: 700, color: EL.darkInk }}>{title}</div>
      {sub && <div style={{ fontFamily: F.body, fontSize: 13.5, color: "rgba(253,251,246,0.65)", maxWidth: 320, lineHeight: 1.5 }}>{sub}</div>}
      {onBack && <button onClick={onBack} style={{ marginTop: 8, border: "1px solid rgba(245,241,234,0.25)", background: "rgba(245,241,234,0.06)", color: EL.darkInk, borderRadius: 11, padding: "9px 18px", fontFamily: F.body, fontWeight: 800, fontSize: 13, cursor: "pointer" }}>Back to Home</button>}
    </div>
  );
}
